import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { CalendarRange, Users, Baby, ChevronLeft, ChevronRight, UserX, RotateCcw } from 'lucide-react';
import { structuresApi, planningApi } from '../../utils/api';

const fmtMin = (min) => { const h = Math.floor(min / 60); const m = min % 60; return `${h}h${m ? String(m).padStart(2, '0') : ''}`; };
const isoOf = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
const mondayISO = (base) => { const x = base ? new Date(base + 'T00:00:00') : new Date(); const off = (x.getDay() + 6) % 7; x.setDate(x.getDate() - off); return isoOf(x); };
const shiftWeek = (mon, delta) => { const x = new Date(mon + 'T00:00:00'); x.setDate(x.getDate() + 7 * delta); return isoOf(x); };
// Numéro de semaine ISO (pour distinguer semaine paire / impaire selon le vrai calendrier)
const isoWeekNum = (iso) => {
  const d = new Date(iso + 'T00:00:00');
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day + 3); // jeudi de la semaine ISO
  const firstThu = new Date(d.getFullYear(), 0, 4);
  return 1 + Math.round((d - firstThu) / (7 * 24 * 3600 * 1000));
};
const fmtDate = (iso) => { try { return new Date(iso + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }); } catch { return iso; } };

function slotColor(count, capacite, capMax) {
  if (count === 0) return '#eef1f6';
  if (count > capMax) return '#dc2626';
  if (count > capacite) return '#f59e0b';
  return '#3f8f5b';
}

// Couleur stable par enfant (lignes nominatives du planning d'occupation)
const CHILD_COLORS = ['#4f83cc', '#9b6adc', '#3f8f5b', '#d99b3f', '#d16a8a', '#3fa3a3', '#6a6fd1', '#d97a3f', '#5aa17f', '#c76ba8', '#8caf3f', '#4a97c2'];

function DayRow({ day, structure, onAbsent, onRestore }) {
  const { capacite, capacite_max_jour: capMax, ouverture, step } = structure;
  const [open, setOpen] = useState(false);
  const max = Math.max(capMax, day.peak, 1);
  const badge = day.depassementMax
    ? { t: 'Dépassement plafond', c: 'bg-red-100 text-red-700' }
    : day.depassementAgrement
      ? { t: 'Surnombre toléré', c: 'bg-amber-100 text-amber-700' }
      : day.peak === 0 ? { t: 'Vide', c: 'bg-gray-100 text-gray-400' }
        : { t: 'Dans l’agrément', c: 'bg-emerald-100 text-emerald-700' };

  return (
    <div className="apple-card p-4">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="font-serif text-[17px] text-navy-900">{day.jour} <span className="text-navy-300 text-[14px] font-sans">{fmtDate(day.date)}</span></div>
        <div className="flex items-center gap-3 text-[12.5px]">
          <span className="flex items-center gap-1 text-navy-600"><Baby size={14} className="text-gold-600" /> pic <strong>{day.peak}</strong>/{capacite}</span>
          <span className="flex items-center gap-1 text-navy-600"><Users size={14} className="text-gold-600" /> <strong>{day.proRequis}</strong> pro{day.proRequis > 1 ? 's' : ''}</span>
          <span className={`px-2 py-0.5 rounded-full ${badge.c}`}>{badge.t}</span>
        </div>
      </div>
      <div className="flex items-end gap-[1px] h-24 border-b border-cream-300">
        {day.slots.map((count, i) => (
          <div key={i} className={`flex-1 group relative flex items-end ${i > 0 && (ouverture + i * step) % 60 === 0 ? 'border-l border-cream-300' : ''}`} style={{ height: '100%' }}>
            <div className="w-full rounded-t-sm" style={{ height: `${(count / max) * 100}%`, backgroundColor: slotColor(count, capacite, capMax) }} />
            <div className="hidden group-hover:block absolute -top-6 left-1/2 -translate-x-1/2 z-10 bg-navy-900 text-white text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap">
              {fmtMin(ouverture + i * step)} · {count} enf.
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-[1px] text-[9px] text-navy-300 mt-1">
        {day.slots.map((_, i) => {
          const t = ouverture + i * step;
          return <div key={i} className="flex-1 text-center whitespace-nowrap">{(t - ouverture) % 60 === 0 ? fmtMin(t) : ''}</div>;
        })}
      </div>

      {/* Lignes nominatives : un enfant par ligne, nom + barre horaire */}
      {day.enfants.length > 0 && (
        <div className="mt-3 space-y-[3px]">
          {day.enfants.map((c, ci) => {
            const color = CHILD_COLORS[ci % CHILD_COLORS.length];
            return (
              <div key={c.id} className="flex items-center gap-2">
                <div className="w-[104px] min-w-[104px] flex items-center gap-1.5 text-[11.5px] text-navy-700">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: color }} />
                  <span className="truncate">{c.prenom}{c.exception === 'presence' ? ' *' : ''}</span>
                </div>
                <div className="flex-1 flex items-center gap-[1px] h-4">
                  {day.slots.map((_, i) => {
                    const t = ouverture + i * step;
                    const on = c.debutMin != null && t >= c.debutMin && t < c.finMin;
                    const hb = i > 0 && (ouverture + i * step) % 60 === 0 ? 'border-l border-cream-300' : '';
                    return <div key={i} className={`flex-1 h-full rounded-[1px] ${hb}`} style={{ background: on ? color : '#eef1f6' }} />;
                  })}
                </div>
                <div className="w-[86px] text-right text-[10.5px] text-navy-400 tabular-nums">{fmtMin(c.debutMin)}–{fmtMin(c.finMin)}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Présences / absences */}
      {(day.enfants.length > 0 || day.absents.length > 0) && (
        <div className="mt-2">
          <button onClick={() => setOpen(o => !o)} className="text-[12px] text-navy-500 hover:text-navy-800">
            {open ? '▾' : '▸'} {day.enfants.length} présent{day.enfants.length > 1 ? 's' : ''}{day.absents.length ? ` · ${day.absents.length} absent${day.absents.length > 1 ? 's' : ''}` : ''}
          </button>
          {open && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {day.enfants.map(e => (
                <span key={'p' + e.id} className={`inline-flex items-center gap-1 text-[12px] px-2 py-1 rounded-lg border ${e.exception === 'presence' ? 'bg-sky-50 border-sky-200 text-sky-700' : 'bg-cream-50 border-cream-300 text-navy-700'}`}>
                  {e.prenom} {e.nom}
                  <button onClick={() => onAbsent(e.id, day.date)} title="Marquer absent" className="text-navy-300 hover:text-red-500"><UserX size={13} /></button>
                </span>
              ))}
              {day.absents.map(e => (
                <span key={'a' + e.id} className="inline-flex items-center gap-1 text-[12px] px-2 py-1 rounded-lg border border-red-100 bg-red-50 text-red-400 line-through">
                  {e.prenom} {e.nom}
                  <button onClick={() => onRestore(e.exception_id)} title="Rétablir la présence" className="text-red-300 hover:text-emerald-600 no-underline"><RotateCcw size={13} /></button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PrevDayRow({ day, structure }) {
  const { capacite, capacite_max_jour: capMax, ouverture, step } = structure;
  const [open, setOpen] = useState(false);
  const max = Math.max(capMax, day.peakTot, 1);
  const pct = (n) => `${(n / max) * 100}%`;
  const nbSur = day.peakSur - day.peakConf;
  const nbProv = day.peakTot - day.peakSur;
  const status = day.depassementMaxAvecDemandes
    ? { t: '⚠️ Dépassement plafond', c: 'bg-red-100 text-red-700' }
    : day.depassementAvecDemandes
      ? { t: 'Dépasse l’agrément si tout accepté', c: 'bg-amber-100 text-amber-700' }
      : day.complet
        ? { t: 'Complet (places sûres)', c: 'bg-navy-100 text-navy-700' }
        : { t: `${day.placesRestantes} place${day.placesRestantes > 1 ? 's' : ''} restante${day.placesRestantes > 1 ? 's' : ''}`, c: 'bg-emerald-100 text-emerald-700' };

  return (
    <div className="apple-card p-4">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="font-serif text-[17px] text-navy-900">{day.jour} <span className="text-navy-300 text-[14px] font-sans">{fmtDate(day.date)}</span></div>
        <div className="flex items-center gap-2.5 text-[12.5px] flex-wrap">
          <span className="text-navy-600"><Baby size={13} className="inline text-emerald-600 mb-0.5" /> {day.peakConf} inscrit{day.peakConf > 1 ? 's' : ''}</span>
          {nbSur > 0 && <span className="text-gold-700">🔒 +{nbSur} sûr</span>}
          {nbProv > 0 && <span className="text-navy-400">◌ +{nbProv} provisoire{nbProv > 1 ? 's' : ''}</span>}
          <span className="text-navy-300">/ {capacite}</span>
          <span className={`px-2 py-0.5 rounded-full font-medium ${status.c}`}>{status.t}</span>
        </div>
      </div>
      <div className="flex items-end gap-[1px] h-24 border-b border-cream-300 relative">
        <div className="absolute left-0 right-0 border-t border-dashed border-navy-400/70 z-[1]" style={{ bottom: pct(capacite) }} title={`Agrément ${capacite}`} />
        {day.slotsConf.map((c, i) => {
          const sv = day.slotsSur[i]; const pv = day.slotsProv[i];
          return (
            <div key={i} className={`flex-1 group relative flex flex-col justify-end ${i > 0 && (ouverture + i * step) % 60 === 0 ? 'border-l border-cream-300' : ''}`} style={{ height: '100%' }}>
              {pv > 0 && <div className="w-full rounded-t-sm" style={{ height: pct(pv), background: 'repeating-linear-gradient(45deg,#cbb488,#cbb488 3px,#ece3cf 3px,#ece3cf 6px)' }} />}
              {sv > 0 && <div className="w-full" style={{ height: pct(sv), backgroundColor: '#c0985a' }} />}
              {c > 0 && <div className="w-full" style={{ height: pct(c), backgroundColor: '#3f8f5b' }} />}
              <div className="hidden group-hover:block absolute -top-9 left-1/2 -translate-x-1/2 z-10 bg-navy-900 text-white text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap">
                {fmtMin(ouverture + i * step)} · {c} conf.{sv ? ` +${sv} sûr` : ''}{pv ? ` +${pv} prov.` : ''}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex gap-[1px] text-[9px] text-navy-300 mt-1">
        {day.slotsConf.map((_, i) => {
          const t = ouverture + i * step;
          return <div key={i} className="flex-1 text-center whitespace-nowrap">{(t - ouverture) % 60 === 0 ? fmtMin(t) : ''}</div>;
        })}
      </div>

      {day.demandes.length > 0 && (
        <div className="mt-2">
          <button onClick={() => setOpen(o => !o)} className="text-[12px] text-navy-500 hover:text-navy-800">
            {open ? '▾' : '▸'} {day.demandes.length} demande{day.demandes.length > 1 ? 's' : ''} ce jour
          </button>
          {open && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {day.demandes.map((dm, i) => (
                <span key={i} className={`inline-flex items-center gap-1 text-[12px] px-2 py-1 rounded-lg border ${dm.place_reservee ? 'bg-gold-100 border-gold-300 text-gold-800' : 'bg-white border-cream-300 text-navy-500'}`}>
                  {dm.enfant_attente ? '✅' : (dm.place_reservee ? '🔒' : '◌')} {dm.label}{dm.enfant_attente ? ' (inscrit)' : ''} · {dm.debut}–{dm.fin}{dm.date_entree ? ` · dès ${fmtDate(dm.date_entree)}` : ''}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Petit calendrier déroulant (contrôlé) — se ferme au clic extérieur, à Échap, ou à la sélection.
const MOIS_FR = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
function MiniCalendar({ value, onPick, onClose }) {
  const ref = useRef(null);
  const base = value ? new Date(value + 'T00:00:00') : new Date();
  const [view, setView] = useState(new Date(base.getFullYear(), base.getMonth(), 1));
  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [onClose]);

  const y = view.getFullYear(); const m = view.getMonth();
  const first = new Date(y, m, 1);
  const offset = (first.getDay() + 6) % 7; // lundi=0
  const nbDays = new Date(y, m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < offset; i++) cells.push(null);
  for (let d = 1; d <= nbDays; d++) cells.push(d);
  const iso = (d) => `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

  return (
    <div ref={ref} className="absolute z-30 top-full mt-1 left-1/2 -translate-x-1/2 bg-white rounded-xl shadow-xl border border-cream-300 p-3 w-64">
      <div className="flex items-center justify-between mb-2">
        <button type="button" onClick={() => setView(new Date(y, m - 1, 1))} className="p-1 rounded hover:bg-cream-100 text-navy-500"><ChevronLeft size={15} /></button>
        <div className="text-[13px] font-semibold text-navy-800 capitalize">{MOIS_FR[m]} {y}</div>
        <button type="button" onClick={() => setView(new Date(y, m + 1, 1))} className="p-1 rounded hover:bg-cream-100 text-navy-500"><ChevronRight size={15} /></button>
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-center text-[10px] text-navy-300 mb-1">
        {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((d, i) => <div key={i}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((d, i) => d === null
          ? <div key={i} />
          : <button key={i} type="button" onClick={() => onPick(iso(d))}
              className={`text-[12px] py-1 rounded-md hover:bg-navy-100 ${value === iso(d) ? 'bg-navy-800 text-white hover:bg-navy-800' : 'text-navy-700'}`}>{d}</button>)}
      </div>
    </div>
  );
}

// Vue « par jour » : liste nominative des présences (confirmés + demandes) pour un jour donné.
function DayRoster({ day, structure }) {
  const confs = day.enfantsConf || [];
  const surs = (day.demandes || []).filter(d => d.place_reservee);
  const provs = (day.demandes || []).filter(d => !d.place_reservee);
  const Ligne = ({ icon, cls, label, debut, fin, extra, href }) => (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${cls}`}>
      <span className="text-[13px]">{icon}</span>
      {href
        ? <Link to={href} className="flex-1 text-[13.5px] font-medium text-navy-800 truncate hover:text-navy-900 hover:underline" title="Ouvrir la fiche">{label}</Link>
        : <span className="flex-1 text-[13.5px] font-medium text-navy-800 truncate">{label}</span>}
      {extra}
      <span className="text-[12.5px] text-navy-500 whitespace-nowrap">{debut}–{fin}</span>
    </div>
  );
  const rien = confs.length === 0 && surs.length === 0 && provs.length === 0;
  return (
    <div className="apple-card p-4 mt-3">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="font-serif text-[16px] text-navy-900">Présences du {day.jour.toLowerCase()} <span className="text-navy-300 text-[13px] font-sans">{fmtDate(day.date)}</span></div>
        <div className="text-[12.5px] text-navy-500">
          Pic : <strong>{day.peakTot}</strong> / {structure.capacite} · encadrement requis <strong>{day.proRequisTot}</strong> pro{day.proRequisTot > 1 ? 's' : ''}
        </div>
      </div>
      {rien ? (
        <div className="text-center text-navy-300 text-[13px] py-4">Aucune présence ce jour.</div>
      ) : (
        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-emerald-700 font-semibold mb-1.5">Inscrits ({confs.length})</div>
            <div className="flex flex-col gap-1.5">
              {confs.length ? confs.map((c, i) => <Ligne key={i} icon="👶" cls="bg-emerald-50 border-emerald-200" label={c.label} debut={c.debut} fin={c.fin} href={c.id ? `/enfants/${c.id}` : null} />) : <div className="text-[12px] text-navy-300">—</div>}
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-gold-700 font-semibold mb-1.5">Places bloquées ({surs.length})</div>
            <div className="flex flex-col gap-1.5">
              {surs.length ? surs.map((c, i) => <Ligne key={i} icon={c.enfant_attente ? '✅' : '🔒'} cls="bg-gold-100 border-gold-300" label={c.enfant_attente ? `${c.label} (inscrit)` : c.label} debut={c.debut} fin={c.fin} href={c.enfant_attente && c.enfant_id ? `/enfants/${c.enfant_id}` : '/demandes'} extra={c.date_entree ? <span className="text-[11px] text-gold-700 whitespace-nowrap">dès {fmtDate(c.date_entree)}</span> : null} />) : <div className="text-[12px] text-navy-300">—</div>}
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-navy-400 font-semibold mb-1.5">Demandes provisoires ({provs.length})</div>
            <div className="flex flex-col gap-1.5">
              {provs.length ? provs.map((c, i) => <Ligne key={i} icon="◌" cls="bg-white border-cream-300" label={c.label} debut={c.debut} fin={c.fin} href="/demandes" />) : <div className="text-[12px] text-navy-300">—</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Bande hebdomadaire COMPACTE : les 5 jours côte à côte, tient à l'écran sans scroller.
// Cliquer un jour ouvre son détail. Fonctionne en mode réel et prévisionnel.
function WeekStrip({ data, mode, selected, onSelect }) {
  const { capacite, capacite_max_jour: capMax, ouverture: ouv = 450, step = 15 } = data.structure;
  const isPrev = mode === 'prev';
  const isHourStart = (i) => i > 0 && (ouv + i * step) % 60 === 0;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
      {data.jours.map((day, idx) => {
        const peak = isPrev ? day.peakTot : day.peak;
        const max = Math.max(capMax, peak, 1);
        const depMax = isPrev ? day.depassementMaxAvecDemandes : day.depassementMax;
        const depAgr = isPrev ? day.depassementAvecDemandes : day.depassementAgrement;
        const dot = depMax ? 'bg-red-500' : depAgr ? 'bg-amber-500' : peak === 0 ? 'bg-slate-300' : 'bg-emerald-500';
        const baseSlots = isPrev ? day.slotsConf : day.slots;
        return (
          <button key={day.date} onClick={() => onSelect(idx)}
            className={`apple-card p-2.5 text-left transition hover:ring-2 hover:ring-gold-300 ${selected === idx ? 'ring-2 ring-gold-400' : ''}`}>
            <div className="flex items-center justify-between">
              <span className="text-[13px] font-serif text-navy-900">{day.jour}</span>
              <span className={`w-2 h-2 rounded-full ${dot}`} />
            </div>
            <div className="text-[11px] text-navy-300 mb-1.5">{fmtDate(day.date)}</div>
            <div className="flex items-end gap-[1px] h-16 border-b border-cream-300">
              {baseSlots.map((c, i) => {
                const hb = isHourStart(i) ? 'border-l border-cream-300/70' : '';
                if (isPrev) {
                  const sv = day.slotsSur[i]; const pv = day.slotsProv[i];
                  return (
                    <div key={i} className={`flex-1 flex flex-col justify-end ${hb}`} style={{ height: '100%' }}>
                      {pv > 0 && <div className="w-full" style={{ height: `${(pv / max) * 100}%`, background: 'repeating-linear-gradient(45deg,#cbb488,#cbb488 2px,#ece3cf 2px,#ece3cf 4px)' }} />}
                      {sv > 0 && <div className="w-full" style={{ height: `${(sv / max) * 100}%`, backgroundColor: '#c0985a' }} />}
                      {c > 0 && <div className="w-full" style={{ height: `${(c / max) * 100}%`, backgroundColor: '#3f8f5b' }} />}
                    </div>
                  );
                }
                return (
                  <div key={i} className={`flex-1 flex items-end ${hb}`} style={{ height: '100%' }}>
                    <div className="w-full" style={{ height: `${(c / max) * 100}%`, backgroundColor: slotColor(c, capacite, capMax) }} />
                  </div>
                );
              })}
            </div>
            {/* Règle horaire : un repère par heure (7h30 → 18h30) */}
            <div className="relative h-3 mt-0.5">
              {(() => {
                const nb = baseSlots.length; const fer = ouv + nb * step; const ticks = [];
                for (let h = Math.ceil(ouv / 60); h * 60 <= fer; h++) ticks.push(h);
                return ticks.map(h => (
                  <span key={h} className="absolute top-0 text-[7.5px] leading-none text-navy-300 -translate-x-1/2" style={{ left: `${((h * 60 - ouv) / (nb * step)) * 100}%` }}>{h}</span>
                ));
              })()}
            </div>
            <div className="mt-1 flex items-center justify-between gap-1">
              <span className="text-[12px] text-navy-700 whitespace-nowrap">pic <strong>{peak}</strong>/{capacite}</span>
              {isPrev
                ? <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${day.complet ? 'bg-navy-100 text-navy-700' : 'bg-emerald-100 text-emerald-700'}`}>{day.complet ? 'complet' : `${day.placesRestantes} pl.`}</span>
                : <span className="text-[11px] text-navy-500 whitespace-nowrap">{day.proRequis} pro</span>}
            </div>
          </button>
        );
      })}
    </div>
  );
}

export default function PlanningPage() {
  const location = useLocation();
  const modeFromUrl = () => (new URLSearchParams(location.search).get('vue') === 'previsionnel' ? 'prev' : 'reel');
  const [structures, setStructures] = useState([]);
  const [selId, setSelId] = useState(null);
  const [week, setWeek] = useState(mondayISO());
  const [showCal, setShowCal] = useState(false);
  const [mode, setMode] = useState(modeFromUrl()); // 'reel' | 'prev'
  const [prevVue, setPrevVue] = useState('semaine'); // 'semaine' (5 jours) | 'jour'
  const [jourSel, setJourSel] = useState(0); // index 0..4 pour la vue par jour (prévisionnel)
  const [jourSelReel, setJourSelReel] = useState(0); // jour sélectionné pour le détail (occupation réelle)
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Semaine paire / impaire déduite du VRAI numéro de semaine ISO de la semaine affichée.
  const semaineNum = isoWeekNum(week);
  const semaine = semaineNum % 2 === 0 ? 'paire' : 'impaire';

  // Synchronise le mode avec l'URL (ligne « Planning prévisionnel » du menu → ?vue=previsionnel)
  useEffect(() => {
    const m = modeFromUrl();
    setMode(prev => { if (prev !== m) { setData(null); return m; } return prev; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    structuresApi.getAll().then(({ data }) => {
      setStructures(data || []);
      if (data && data.length) setSelId(data[0].id);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const fetchData = useCallback(() => {
    if (!selId) return;
    const p = mode === 'prev'
      ? planningApi.getPrevisionnel(selId, week, semaine)
      : planningApi.getOccupation(selId, week);
    p.then(({ data }) => setData(data)).catch(() => setData(null));
  }, [selId, week, mode, semaine]);
  useEffect(() => { setData(null); fetchData(); }, [fetchData]);

  const markAbsent = async (enfantId, date) => {
    await planningApi.addException({ enfant_id: enfantId, structure_id: selId, date, type: 'absence' });
    fetchData();
  };
  const restore = async (exId) => { await planningApi.deleteException(exId); fetchData(); };

  if (loading) return <div className="py-16 text-center text-navy-300">Chargement…</div>;

  const isThisWeek = week === mondayISO();

  return (
    <div className="max-w-5xl">
      <div className="mb-4">
        <h1 className="font-serif text-[30px] leading-tight text-navy-900 flex items-center gap-2"><CalendarRange size={26} className="text-gold-600" /> Planning d’occupation</h1>
        <div className="w-12 h-0.5 bg-gold-400 rounded-full mt-2 mb-2.5" />
        <p className="text-navy-400 text-[14px]">
          {mode === 'prev'
            ? 'Occupation prévisionnelle : enfants inscrits + demandes de préinscription. Distingue les places déjà bloquées (sûres) des simples demandes, et indique s’il reste de la place.'
            : 'Présence des enfants par crèche — calculée depuis leurs contrats, ajustable au jour le jour (absences). Encadrement : 1 pro / 6 enfants, 2 minimum.'}
        </p>
      </div>

      {/* Sélecteur de mode */}
      <div className="inline-flex p-1 bg-cream-100 border border-cream-300 rounded-xl mb-4">
        <button onClick={() => { if (mode !== 'reel') { setData(null); setMode('reel'); } }}
          className={`px-4 py-1.5 rounded-lg text-[13.5px] font-medium transition-colors ${mode === 'reel' ? 'bg-white text-navy-900 shadow-sm' : 'text-navy-400 hover:text-navy-700'}`}>
          Occupation réelle
        </button>
        <button onClick={() => { if (mode !== 'prev') { setData(null); setMode('prev'); } }}
          className={`px-4 py-1.5 rounded-lg text-[13.5px] font-medium transition-colors ${mode === 'prev' ? 'bg-white text-navy-900 shadow-sm' : 'text-navy-400 hover:text-navy-700'}`}>
          Prévisionnel (avec demandes)
        </button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex flex-wrap gap-2">
          {structures.map(s => (
            <button key={s.id} onClick={() => setSelId(s.id)}
              className={`px-4 py-2 rounded-xl text-[14px] border transition-colors ${selId === s.id ? 'bg-navy-800 text-white border-navy-800' : 'bg-white text-navy-600 border-cream-300 hover:border-navy-300'}`}>
              {s.nom}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={() => setWeek(w => shiftWeek(w, -1))} className="apple-btn-secondary p-2"><ChevronLeft size={16} /></button>
          <div className="px-2 min-w-[180px] text-center relative">
            <button type="button"
              onClick={() => setShowCal(v => !v)}
              title="Choisir une date"
              className="w-full rounded-lg px-1 py-0.5 hover:bg-cream-100 transition-colors cursor-pointer">
              <div className="text-[13px] text-navy-600 flex items-center justify-center gap-1">
                <CalendarRange size={13} className="text-navy-400" />
                Semaine du <strong>{fmtDate(week)}</strong>
              </div>
              <div className="flex items-center justify-center gap-1.5 mt-0.5">
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${semaine === 'paire' ? 'bg-navy-100 text-navy-700' : 'bg-gold-100 text-gold-800'}`}>
                  Semaine {semaineNum} · {semaine}
                </span>
                {isThisWeek && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">en cours</span>}
              </div>
            </button>
            {showCal && (
              <MiniCalendar value={week}
                onPick={(iso) => { setWeek(mondayISO(iso)); setShowCal(false); }}
                onClose={() => setShowCal(false)} />
            )}
          </div>
          <button onClick={() => setWeek(w => shiftWeek(w, 1))} className="apple-btn-secondary p-2"><ChevronRight size={16} /></button>
          {!isThisWeek && <button onClick={() => setWeek(mondayISO())} className="apple-btn-secondary text-[12px]">Cette semaine</button>}
        </div>
      </div>

      {!data ? (
        <div className="py-12 text-center text-navy-300">Chargement du planning…</div>
      ) : mode === 'prev' ? (
        (data.nb_enfants_actifs === 0 && data.nb_demandes === 0) ? (
          <div className="apple-card p-8 text-center text-navy-400">
            Aucun enfant ni demande pour cette crèche.<br />
            <span className="text-[13px] text-navy-300">Le prévisionnel se remplit dès que vous recevez des demandes de préinscription.</span>
          </div>
        ) : (
          <>
            <div className="apple-card p-4 mb-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[13px]">
              <span className="text-navy-600"><Baby size={14} className="inline text-emerald-600 mb-0.5" /> <strong>{data.nb_enfants_actifs}</strong> inscrit{data.nb_enfants_actifs > 1 ? 's' : ''}</span>
              <span className="text-gold-700">🔒 <strong>{data.nb_places_bloquees}</strong> place{data.nb_places_bloquees > 1 ? 's' : ''} bloquée{data.nb_places_bloquees > 1 ? 's' : ''}{data.nb_enfants_attente > 0 ? ` (dont ${data.nb_enfants_attente} inscrit${data.nb_enfants_attente > 1 ? 's' : ''} en attente)` : ''}</span>
              <span className="text-navy-400">◌ <strong>{data.nb_demandes - data.nb_places_bloquees}</strong> demande{(data.nb_demandes - data.nb_places_bloquees) > 1 ? 's' : ''} provisoire{(data.nb_demandes - data.nb_places_bloquees) > 1 ? 's' : ''}</span>
              <span className="text-navy-300 ml-auto">Agrément {data.structure.capacite} · max {data.structure.capacite_max_jour}/jour</span>
            </div>
            <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-[12px] text-navy-400 mb-3">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm inline-block" style={{ background: '#3f8f5b' }} /> Inscrits</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm inline-block" style={{ background: '#c0985a' }} /> Place bloquée (sûr)</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm inline-block" style={{ background: 'repeating-linear-gradient(45deg,#cbb488,#cbb488 3px,#ece3cf 3px,#ece3cf 6px)' }} /> Demande provisoire</span>
              <span className="flex items-center gap-1"><span className="w-4 border-t border-dashed border-navy-400 inline-block" /> Agrément</span>
            </div>
            {/* Contrôles prévisionnel : semaine paire/impaire + vue semaine/jour */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <div className="inline-flex p-1 bg-cream-100 border border-cream-300 rounded-xl">
                <button onClick={() => { if (semaine !== 'paire') setWeek(w => shiftWeek(w, 1)); }}
                  className={`px-3 py-1.5 rounded-lg text-[13px] font-medium transition-colors ${semaine === 'paire' ? 'bg-white text-navy-900 shadow-sm' : 'text-navy-400 hover:text-navy-700'}`}>
                  Semaine paire <span className="text-navy-300">(A)</span>
                </button>
                <button onClick={() => { if (semaine !== 'impaire') setWeek(w => shiftWeek(w, 1)); }}
                  className={`px-3 py-1.5 rounded-lg text-[13px] font-medium transition-colors ${semaine === 'impaire' ? 'bg-white text-navy-900 shadow-sm' : 'text-navy-400 hover:text-navy-700'}`}>
                  Semaine impaire <span className="text-navy-300">(B)</span>
                </button>
              </div>
              <div className="inline-flex p-1 bg-cream-100 border border-cream-300 rounded-xl">
                <button onClick={() => setPrevVue('semaine')}
                  className={`px-3 py-1.5 rounded-lg text-[13px] font-medium transition-colors ${prevVue === 'semaine' ? 'bg-white text-navy-900 shadow-sm' : 'text-navy-400 hover:text-navy-700'}`}>
                  Vue semaine
                </button>
                <button onClick={() => setPrevVue('jour')}
                  className={`px-3 py-1.5 rounded-lg text-[13px] font-medium transition-colors ${prevVue === 'jour' ? 'bg-white text-navy-900 shadow-sm' : 'text-navy-400 hover:text-navy-700'}`}>
                  Vue par jour
                </button>
              </div>
              {prevVue === 'jour' && (
                <div className="inline-flex gap-1 flex-wrap">
                  {data.jours.map((d, i) => (
                    <button key={d.date} onClick={() => setJourSel(i)}
                      className={`px-3 py-1.5 rounded-lg text-[12.5px] border transition-colors ${jourSel === i ? 'bg-navy-800 text-white border-navy-800' : 'bg-white text-navy-600 border-cream-300 hover:border-navy-300'}`}>
                      {d.jour}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {prevVue === 'semaine' ? (
              <WeekStrip data={data} mode="prev" selected={-1} onSelect={(i) => { setJourSel(i); setPrevVue('jour'); }} />
            ) : (
              <div>
                <PrevDayRow key={data.jours[jourSel].date} day={data.jours[jourSel]} structure={data.structure} />
                <DayRoster day={data.jours[jourSel]} structure={data.structure} />
              </div>
            )}
          </>
        )
      ) : data.nb_enfants_actifs === 0 ? (
        <div className="apple-card p-8 text-center text-navy-400">
          Aucun enfant actif dans cette crèche.<br />
          <span className="text-[13px] text-navy-300">Le planning se remplit automatiquement dès que des enfants ont un contrat.</span>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-4 text-[12px] text-navy-400 mb-3">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm inline-block" style={{ background: '#3f8f5b' }} /> Agrément (≤{data.structure.capacite})</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm inline-block" style={{ background: '#f59e0b' }} /> Surnombre (≤{data.structure.capacite_max_jour})</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm inline-block" style={{ background: '#dc2626' }} /> Dépassement</span>
          </div>
          <WeekStrip data={data} mode="reel" selected={jourSelReel} onSelect={setJourSelReel} />
          {data.jours[jourSelReel] && (
            <div className="mt-3">
              <DayRow key={data.jours[jourSelReel].date} day={data.jours[jourSelReel]} structure={data.structure} onAbsent={markAbsent} onRestore={restore} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
